import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ChevronRight, Loader2, Flame } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { CinematicNav } from "../components/CinematicNav";
import { CartDrawer } from "../components/CartDrawer";
import { toast } from "sonner";

const CAT_ORDER = ["burgers", "sides", "extras", "drinks"];
const CAT_META = {
    burgers: { label: "BURGERS",  sub: "Signature smash burgers" },
    sides:   { label: "SIDES",    sub: "Fries & loaded sides" },
    extras:  { label: "EXTRAS",   sub: "Add-ons & dips" },
    drinks:  { label: "DRINKS",   sub: "Refreshments" },
    desserts:{ label: "DESSERTS", sub: "Sweet endings" },
};

export const BurgerMenu = () => {
    const navigate = useNavigate();
    const { add } = useCart();
    const [store, setStore] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cartOpen, setCartOpen] = useState(false);
    const [active, setActive] = useState("burgers");

    useEffect(() => {
        api.get("/stores/grill-bros").then(({ data }) => {
            setStore(data);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    const grouped = useMemo(() => {
        if (!store) return {};
        const map = {};
        (store.menu || []).forEach((it) => {
            if (!map[it.category]) map[it.category] = [];
            map[it.category].push(it);
        });
        return map;
    }, [store]);

    const categories = useMemo(() => {
        const present = Object.keys(grouped).sort((a, b) => {
            const ai = CAT_ORDER.indexOf(a); const bi = CAT_ORDER.indexOf(b);
            return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
        });
        return present;
    }, [grouped]);

    useEffect(() => {
        if (categories.length && !categories.includes(active)) setActive(categories[0]);
    }, [categories, active]);

    const items = grouped[active] || [];

    const handleAdd = (it) => {
        add({ id: it.id, name: it.name, price: it.price, image_url: it.image_url, category: it.category }, { silent: true });
        toast.success(`${it.name} → cart`, { duration: 1500 });
    };

    if (loading) {
        return (
            <div className="burger-bg min-h-screen grid place-items-center">
                <Loader2 className="animate-spin text-[var(--burger-orange-2)]" size={36} />
            </div>
        );
    }

    return (
        <div data-testid="burger-menu" className="burger-bg min-h-screen relative">
            <CinematicNav onOpenCart={() => setCartOpen(true)} />
            <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />

            {/* Hero */}
            <section className="relative pt-32 sm:pt-40 pb-10 px-5 sm:px-10 max-w-7xl mx-auto">
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="font-display text-xs tracking-[0.5em] ember-text mb-3"
                >
                    // THE FULL LINEUP
                </motion.p>
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="font-display text-6xl sm:text-8xl leading-[0.95]"
                >
                    MENU.
                </motion.h1>
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.25 }}
                    className="text-white/55 mt-3 max-w-md"
                >
                    Επιλέξτε από τα signature smash burgers, sides, extras και drinks μας.
                </motion.p>
            </section>

            {/* Sticky category tabs */}
            <div data-testid="menu-tabs" className="sticky top-[88px] z-40 px-5 sm:px-10 mb-6">
                <div className="max-w-7xl mx-auto">
                    <div className="flex gap-1 p-1 rounded-full glass-burger overflow-x-auto cinematic-scrollbar">
                        {categories.map((c) => {
                            const on = c === active;
                            const meta = CAT_META[c] || { label: c.toUpperCase() };
                            const count = grouped[c]?.length || 0;
                            return (
                                <button
                                    key={c}
                                    onClick={() => setActive(c)}
                                    data-testid={`tab-${c}`}
                                    className={`relative shrink-0 px-5 h-11 rounded-full font-display tracking-widest text-xs transition flex items-center gap-2 ${
                                        on ? "text-black" : "text-white/75 hover:text-white"
                                    }`}
                                >
                                    {on && (
                                        <motion.span
                                            layoutId="menu-tab-pill"
                                            className="absolute inset-0 -z-10 rounded-full ember-gradient"
                                            transition={{ type: "spring", stiffness: 380, damping: 30 }}
                                        />
                                    )}
                                    {meta.label}
                                    <span className={`text-[10px] ${on ? "text-black/60" : "text-white/40"}`}>·{count}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Items grid */}
            <section data-testid="menu-grid" className="px-5 sm:px-10 pb-32 max-w-7xl mx-auto">
                <div className="mb-4">
                    <p className="text-xs text-white/45 font-mono uppercase tracking-widest">
                        {CAT_META[active]?.sub || ""}
                    </p>
                </div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={active}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.4 }}
                        className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
                    >
                        {items.map((it, i) => (
                            <MenuCard key={it.id} item={it} index={i} onAdd={() => handleAdd(it)} />
                        ))}
                    </motion.div>
                </AnimatePresence>

                {items.length === 0 && (
                    <div className="text-center py-16">
                        <Flame size={36} className="mx-auto text-white/15 mb-3" />
                        <p className="font-display text-2xl text-white/40">NO ITEMS</p>
                    </div>
                )}
            </section>
        </div>
    );
};

const MenuCard = ({ item, index, onAdd }) => (
    <motion.article
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.04 }}
        data-testid={`menu-item-${item.id}`}
        className="burger-card glass-burger rounded-3xl overflow-hidden flex sm:flex-col"
    >
        <div className="relative w-32 sm:w-full sm:h-48 shrink-0 overflow-hidden">
            <img
                src={item.image_url}
                alt={item.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        </div>
        <div className="flex-1 p-4 flex flex-col">
            <h3 className="font-display text-lg sm:text-xl tracking-wide leading-tight line-clamp-1">
                {item.name.toUpperCase()}
            </h3>
            <p className="text-[11px] text-white/50 leading-relaxed mt-1 line-clamp-2 min-h-[2.4em]">
                {item.description}
            </p>
            <div className="flex items-center justify-between mt-3 sm:mt-auto pt-3">
                <span className="font-display text-2xl ember-text">€{item.price.toFixed(2)}</span>
                <motion.button
                    whileTap={{ scale: 0.92 }}
                    whileHover={{ scale: 1.1 }}
                    onClick={onAdd}
                    data-testid={`menu-add-${item.id}`}
                    className="h-10 w-10 grid place-items-center rounded-full ember-gradient text-black shadow-[0_6px_20px_rgba(255,138,0,0.35)]"
                >
                    <Plus size={16} strokeWidth={3} />
                </motion.button>
            </div>
        </div>
    </motion.article>
);

export default BurgerMenu;
